import { useCallback, useEffect, useMemo } from "react"
import { useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@f2e/ui/components/base"

import { SUPPORT_EMAIL, getSupportMailto } from "@/lib/support"

const PaymentReturnBridge = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const paymentResult = searchParams.get("payment_result") || "unknown"
  const paymentMessage = searchParams.get("payment_message") || ""
  const paymentErrorCode = searchParams.get("payment_error_code") || "0"
  const paymentRequestNo = searchParams.get("payment_request_no") || ""

  const isSuccess = paymentResult === "success"
  const isFailed = paymentResult === "failed"

  const messagePayload = useMemo(
    () => ({
      type: "codeer.payment.return",
      payment_result: paymentResult,
      payment_error_code: paymentErrorCode,
      payment_message: paymentMessage,
      payment_request_no: paymentRequestNo,
      ts: Date.now(),
    }),
    [paymentErrorCode, paymentMessage, paymentRequestNo, paymentResult]
  )

  const notifyParent = useCallback(() => {
    try {
      localStorage.setItem(
        "codeer.payment.return",
        JSON.stringify(messagePayload)
      )
    } catch {
      // no-op
    }

    if (!window.opener) return
    try {
      window.opener.postMessage(messagePayload, window.location.origin)
    } catch {
      // no-op
    }
  }, [messagePayload])

  useEffect(() => {
    notifyParent()
    return undefined
  }, [notifyParent])

  const handleBack = () => {
    notifyParent()
    window.close()
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-950">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {isSuccess
              ? t("common.paymentBridge.successTitle")
              : isFailed
                ? t("common.paymentBridge.errorTitle")
                : t("common.paymentBridge.unknownTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {isSuccess
              ? t("common.paymentBridge.successDescription")
              : isFailed
                ? t("common.paymentBridge.errorDescription")
                : t("common.paymentBridge.unknownDescription")}
          </p>
          {paymentRequestNo && (
            <p className="text-xs text-muted-foreground">
              {t("common.paymentBridge.requestNo")}:{" "}
              <span className="font-mono">{paymentRequestNo}</span>
            </p>
          )}
          {(isFailed || !isSuccess) && paymentMessage && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {paymentMessage} ({paymentErrorCode})
            </p>
          )}
          {!isSuccess && (
            <p className="text-xs text-muted-foreground">
              {t("common.support.paymentHelp")}{" "}
              <a
                className="font-medium text-primary underline-offset-4 hover:underline"
                href={getSupportMailto(
                  `Payment issue${paymentRequestNo ? ` ${paymentRequestNo}` : ""}`
                )}
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
          )}
          <Button type="button" onClick={handleBack}>
            {t("common.paymentBridge.backToChat")}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default PaymentReturnBridge
